import { useCallback } from 'react';
import type { Resource } from '@/types';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useUser } from '@/contexts/UserContext';
import {
  scopeResourcesByAccess,
  scopedUserIdentities,
  type ReportViewAccess,
} from '@/lib/reportScope';

/**
 * Resolves the per-report data-visibility scope ("All" / "Team" / "Own only")
 * for the logged-in user from their role's report permissions, and exposes
 * helpers that apply that scope to the report's data.
 *
 * `scopeResources` filters a resource list; `getUserIdentities` returns the set
 * of user names/emails visible at the current scope (or `null` for "All"),
 * for activity-style reports that key off the acting user.
 */
export function useReportScope(reportKey: string) {
  const { getReportViewAccessLevel } = usePermissions();
  const { user } = useUser();

  const access: ReportViewAccess = getReportViewAccessLevel(reportKey);
  const userEmail = user.email;
  const userName = user.name;

  const scopeResources = useCallback(
    (resources: Resource[]) => scopeResourcesByAccess(resources, access, userEmail),
    [access, userEmail],
  );

  const getUserIdentities = useCallback(
    (resources: Resource[]) =>
      scopedUserIdentities(resources, access, { name: userName, email: userEmail }),
    [access, userName, userEmail],
  );

  return { access, userEmail, scopeResources, getUserIdentities };
}
